import React, { useEffect, useState } from "react";

// region External libraries
import { Button, useToast } from "@npm-telluria-tecnologia/telluria-ui";
import { useLazyQuery } from "@apollo/client";
// endregion External libraries
// region Helper
import { sortAlphabetically, userClaimTypes } from "src/helpers/utils";
// endregion Helpers
// region Languages
import { GlobalMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
// endregion Languages
// region Atoms
import AttributeColorLegend from "@atoms/AttributeColorLegend";
import Loading from "@atoms/Loading";
// endregion Atoms
// region Molecules
import ClaimGroup from "src/molecules/ClaimGroup";
// endregion Molecules
// region Organisms
// endregion Organisms
// region Pages
// endregion Pages
// region Store
import {
  ClaimGroupQueriesGetAllWithSortedPaginationArgs,
  ClaimGroupType,
  ClaimType,
  ECommandResultStatus,
  ESort,
  EWhereClausesOperators,
  RoleClaimType
} from "src/store/graphql/helpers/types.graphql";
import {
  claimGroupAllWithSortedPagination,
  TClaimGroupAllWithSortedPaginationQueries
} from "src/store/graphql/groups/claimGroup/query.graphql";
// region Store
// region Styles
import * as Styled from "./styles";
// endregion Styles

export enum EPermissionGroupOrigin {
  ROLE,
  USER
}

interface IPermissionGroupProps {
  origin: EPermissionGroupOrigin;
  permissionGroupId: string;
  onClaimGroupsChange: (claimGroups: string[]) => void;
  selectedClaimsOfCurrentRole?: RoleClaimType[];
  selectedClaims?: {claimId: string}[];
}

const PermissionGroup: React.FC<IPermissionGroupProps> = (
  { origin, selectedClaimsOfCurrentRole, permissionGroupId, onClaimGroupsChange, selectedClaims }
) => {
  // region Queries
  const [QueryGetClaimGroupAllWithSortedPagination] = useLazyQuery<TClaimGroupAllWithSortedPaginationQueries,
    ClaimGroupQueriesGetAllWithSortedPaginationArgs>(
      claimGroupAllWithSortedPagination([
        "id", "name", "alias", "order", "claims.id", "claims.alias", "claims.order"
      ])
    );
  // endregion Queries
  // region States
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [claimGroups, setClaimGroups] = useState<ClaimGroupType[]>([] as ClaimGroupType[]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [selectAllButtonStateAfterClick, setSelectAllButtonStateAfterClick] = useState<boolean>(false);
  // endregion States

  // region Hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  // endregion Hooks

  // region Constants
  const claimLegends = [
    {
      color: userClaimTypes.defaultClaim.color,
      caption: t(userClaimTypes.defaultClaim.caption)
    },
    {
      color: userClaimTypes.additionalUserClaim.color,
      caption: t(userClaimTypes.additionalUserClaim.caption)
    },
    {
      color: userClaimTypes.restrictedUserClaim.color,
      caption: t(userClaimTypes.restrictedUserClaim.caption)
    }
  ];
  // endregion Constants

  // region Functions
  const checkIfAllClaimsAreSelected = (updatedClaimGroups: ClaimGroupType[], selectedClaimIds: string[]) => {
    const claimIdsOfAllClaimGroups = updatedClaimGroups.flatMap((claimGroup) => (
        (claimGroup.claims || []) as ClaimType[]).map((claim) => claim.id));

    const areAllClaimsSelected = claimIdsOfAllClaimGroups
      .every((claimId) => selectedClaimIds.some((id) => id === claimId));

    if (areAllClaimsSelected !== selectAll) {
      setSelectAll(areAllClaimsSelected);
    }
  };

  const updateClaimGroup = (claimIds: string[], checked: boolean) => {
    const selectedClaimIds = selectedClaims?.map((selectedClaim) => selectedClaim.claimId) ?? [];

    let claimIdsToSet: string[] = [];

    if (checked) {
      const newClaimIds = claimIds.filter((id) => !selectedClaimIds.includes(id));

      claimIdsToSet = [...selectedClaimIds, ...newClaimIds];
    } else {
      claimIdsToSet = selectedClaimIds.filter((id) => !claimIds.includes(id));
    }

    checkIfAllClaimsAreSelected(claimGroups, claimIdsToSet);

    onClaimGroupsChange(claimIdsToSet);
  };

  const toggleSelectAllClaimsFromClaimGroups = () => {
    const updatedSelectAllClaims = !selectAll;
    const selectedClaimIds = selectedClaims?.map((selectedClaim) => selectedClaim.claimId) ?? [];
    const claimIdsOfAllClaimGroups = claimGroups.flatMap((claimGroup) => (
        (claimGroup.claims || []) as ClaimType[]).map((claim) => claim.id));

    let claimIdsToSet: string[] = [];

    if (updatedSelectAllClaims) {
      const newClaimIds = claimIdsOfAllClaimGroups.filter((id) => !selectedClaimIds.includes(id));

      claimIdsToSet = [...selectedClaimIds, ...newClaimIds];
    } else {
      claimIdsToSet = selectedClaimIds.filter((id) => !claimIdsOfAllClaimGroups.includes(id));
    }

    setSelectAll(updatedSelectAllClaims);
    setSelectAllButtonStateAfterClick(updatedSelectAllClaims);

    onClaimGroupsChange(claimIdsToSet);
  };

  const getClaimGroups = async () => {
    try {
      setIsLoading(true);

      const query = await QueryGetClaimGroupAllWithSortedPagination(
        {
          variables: {
            where: [
              {
                property: "PermissionGroupId",
                operator: EWhereClausesOperators.EQUALS,
                value: permissionGroupId
              }
            ],
            sort: [
              {
                field: "order",
                sortDirection: ESort.ASC
              }
            ]
          }
        }
      );

      const response = query.data?.claimGroup.getAllWithSortedPagination;

      if (response?.status !== ECommandResultStatus.SUCCESS) return [] as ClaimGroupType[];

      return response.result as ClaimGroupType[];
    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });

      return [] as ClaimGroupType[];
    } finally {
      setIsLoading(false);
    }
  };
  // endregion Functions

  useEffect(() => {
    (async () => {
      const claimGroups = await getClaimGroups();

      checkIfAllClaimsAreSelected(claimGroups, selectedClaims?.map((selectedClaim) => selectedClaim.claimId) ?? []);

      setClaimGroups(claimGroups);
    })();
  }, []);

  return (
    <Styled.Container>
      <Loading loading={isLoading} />
      <Styled.Header>
        {origin === EPermissionGroupOrigin.USER && (
          <AttributeColorLegend legends={claimLegends} />
        )}
        <Button
          type="button"
          text={!selectAll
            ? t(GlobalMessages.selectAllText)
            : t(GlobalMessages.removeAllText)}
          className="select-all-permissions-button"
          onClick={toggleSelectAllClaimsFromClaimGroups}
        />
      </Styled.Header>
      <Styled.Attributes>
        {claimGroups.map((claimGroup) => (
          <ClaimGroup
            key={claimGroup.id}
            origin={origin}
            claimGroup={claimGroup}
            selectedClaims={selectedClaims}
            selectAllClaimsFromClaimGroups={selectAllButtonStateAfterClick}
            selectedClaimsOfCurrentRole={selectedClaimsOfCurrentRole}
            onClaimsChange={updateClaimGroup}
          />
        ))}
      </Styled.Attributes>
    </Styled.Container>
  );
};

PermissionGroup.defaultProps = {
  selectedClaimsOfCurrentRole: undefined,
  selectedClaims: undefined
};

export default PermissionGroup;